import { useState, useEffect, useCallback } from 'react';
import { GitBranch, AlertTriangle, Sparkles, Plus, X, ChevronDown } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────

type SuccessionReadiness = 'not_ready' | 'developing' | 'ready';

interface SuccessionPlan {
  id: string;
  role: string;
  role_holder_id?: string;
  role_holder_name?: string;
  department_name?: string;
  primary_successor_id?: string;
  primary_successor_name?: string;
  shadow_id?: string;
  shadow_name?: string;
  readiness: SuccessionReadiness;
  notes?: string;
  development_milestones: string[];
  risk_score: number;
}

interface KeyPersonRisk {
  employee_id: string;
  employee_name: string;
  title: string;
  department: string;
  risk_score: number;
  risk_reasons: string[];
  has_succession_plan: boolean;
  successor_readiness?: SuccessionReadiness;
}

interface Dashboard {
  total_roles: number;
  roles_covered: number;
  critical_gaps: number;
  avg_readiness_score: number;
  readiness_breakdown: { ready: number; developing: number; not_ready: number };
  high_risk_plans: Array<{ id: string; role: string; role_holder_name?: string; readiness: SuccessionReadiness; risk_score: number }>;
}

interface AICandidate {
  employee_id: string;
  employee_name: string;
  title?: string;
  department?: string;
  skills: string[];
  fit_score: number;
}

// ─── Helpers ─────────────────────────────────────────────────

const READINESS_STYLES: Record<SuccessionReadiness, { label: string; badge: string; ring: string }> = {
  not_ready: { label: 'Not Ready', badge: 'bg-red-500/20 text-red-400', ring: '#ef4444' },
  developing: { label: 'Developing', badge: 'bg-amber-500/20 text-amber-400', ring: '#f59e0b' },
  ready: { label: 'Ready', badge: 'bg-green-500/20 text-green-400', ring: '#22c55e' },
};

function ReadinessBadge({ readiness }: { readiness: SuccessionReadiness }) {
  const style = READINESS_STYLES[readiness];
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${style.badge}`}>{style.label}</span>;
}

function riskColor(score: number): string {
  if (score >= 70) return 'text-red-400';
  if (score >= 40) return 'text-amber-400';
  return 'text-green-400';
}

// ─── Talent Pipeline (SVG swim-lane) ─────────────────────────

function TalentPipeline({ plans }: { plans: SuccessionPlan[] }) {
  if (plans.length === 0) return null;

  const rowHeight = 52;
  const totalH = plans.length * rowHeight + 20;

  return (
    <svg viewBox={`0 0 700 ${totalH}`} className="w-full">
      {/* Headers */}
      <text x="10" y="14" fontSize="9" fontWeight="600" fill="#a1a1aa">ROLE HOLDER</text>
      <text x="240" y="14" fontSize="9" fontWeight="600" fill="#a1a1aa">PRIMARY SUCCESSOR</text>
      <text x="470" y="14" fontSize="9" fontWeight="600" fill="#a1a1aa">SHADOW</text>
      {/* Column backgrounds */}
      <rect x="5" y="18" width="220" height={totalH - 18} rx="4" fill="#18181b" />
      <rect x="235" y="18" width="220" height={totalH - 18} rx="4" fill="#18181b" />
      <rect x="465" y="18" width="220" height={totalH - 18} rx="4" fill="#18181b" />

      {plans.map((plan, i) => {
        const y = 18 + i * rowHeight;
        const rStyle = READINESS_STYLES[plan.readiness];
        return (
          <g key={plan.id}>
            {/* Role holder box */}
            <rect x="10" y={y + 5} width="210" height={rowHeight - 10} rx="4" fill="#27272a" />
            <text x="20" y={y + 20} fontSize="11" fontWeight="600" fill="#f4f4f5">{plan.role_holder_name ?? '(Vacant)'}</text>
            <text x="20" y={y + 33} fontSize="9" fill="#a1a1aa">{plan.role}</text>
            {/* Arrow 1 */}
            <path d={`M225,${y + rowHeight / 2} L240,${y + rowHeight / 2}`} stroke="#3f3f46" strokeWidth="1.5" markerEnd="url(#arrow)" />
            {/* Primary successor box */}
            <rect x="240" y={y + 5} width="210" height={rowHeight - 10} rx="4" fill="#27272a" />
            {plan.primary_successor_name ? (
              <>
                <text x="250" y={y + 20} fontSize="11" fontWeight="600" fill="#f4f4f5">{plan.primary_successor_name}</text>
                <text x="250" y={y + 33} fontSize="9" fill={rStyle.ring}>{rStyle.label}</text>
              </>
            ) : (
              <text x="250" y={y + 25} fontSize="10" fill="#ef4444">No successor designated</text>
            )}
            {/* Arrow 2 */}
            <path d={`M455,${y + rowHeight / 2} L470,${y + rowHeight / 2}`} stroke="#3f3f46" strokeWidth="1.5" />
            {/* Shadow box */}
            <rect x="470" y={y + 5} width="210" height={rowHeight - 10} rx="4" fill="#27272a" />
            {plan.shadow_name ? (
              <text x="480" y={y + 20} fontSize="11" fill="#f4f4f5">{plan.shadow_name}</text>
            ) : (
              <text x="480" y={y + 25} fontSize="10" fill="#71717a">No shadow assigned</text>
            )}
          </g>
        );
      })}

      <defs>
        <marker id="arrow" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
          <path d="M0,0 L6,3 L0,6 Z" fill="#3f3f46" />
        </marker>
      </defs>
    </svg>
  );
}

// ─── Create/Edit Plan Modal ───────────────────────────────────

interface PlanModalProps {
  plan?: SuccessionPlan | null;
  onClose: () => void;
  onSaved: () => void;
}

function PlanModal({ plan, onClose, onSaved }: PlanModalProps) {
  const [form, setForm] = useState({
    role: plan?.role ?? '',
    role_holder_name: plan?.role_holder_name ?? '',
    department_name: plan?.department_name ?? '',
    primary_successor_name: plan?.primary_successor_name ?? '',
    shadow_name: plan?.shadow_name ?? '',
    readiness: (plan?.readiness ?? 'not_ready') as SuccessionReadiness,
    notes: plan?.notes ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (!form.role.trim()) { setError('Role is required'); return; }
    setSaving(true);
    setError(null);
    try {
      const url = plan ? `/api/people/succession/plans/${plan.id}` : '/api/people/succession/plans';
      const method = plan ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          role: form.role,
          role_holder_name: form.role_holder_name || undefined,
          department_name: form.department_name || undefined,
          primary_successor_name: form.primary_successor_name || undefined,
          shadow_name: form.shadow_name || undefined,
          readiness: form.readiness,
          notes: form.notes || undefined,
        }),
      });
      if (!res.ok) throw new Error('Failed to save plan');
      onSaved();
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="glass rounded-xl p-6 w-full max-w-lg space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">{plan ? 'Edit Succession Plan' : 'New Succession Plan'}</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>
        {error && <p className="text-red-400 text-sm">{error}</p>}
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="text-xs text-muted-foreground block mb-1">Role / Position</label>
            <input value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground" placeholder="VP of Engineering" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Current Role Holder</label>
            <input value={form.role_holder_name} onChange={e => setForm(f => ({ ...f, role_holder_name: e.target.value }))} className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground" placeholder="Name..." />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Department</label>
            <input value={form.department_name} onChange={e => setForm(f => ({ ...f, department_name: e.target.value }))} className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground" placeholder="Engineering" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Primary Successor</label>
            <input value={form.primary_successor_name} onChange={e => setForm(f => ({ ...f, primary_successor_name: e.target.value }))} className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground" placeholder="Name..." />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Shadow / 2nd Backup</label>
            <input value={form.shadow_name} onChange={e => setForm(f => ({ ...f, shadow_name: e.target.value }))} className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground" placeholder="Name..." />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Readiness</label>
            <select value={form.readiness} onChange={e => setForm(f => ({ ...f, readiness: e.target.value as SuccessionReadiness }))} className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground">
              <option value="not_ready">Not Ready</option>
              <option value="developing">Developing</option>
              <option value="ready">Ready</option>
            </select>
          </div>
          <div className="col-span-2">
            <label className="text-xs text-muted-foreground block mb-1">Notes</label>
            <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground resize-none" />
          </div>
        </div>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-3 py-1.5 rounded-lg text-sm text-muted-foreground hover:bg-surface-3">Cancel</button>
          <button onClick={() => void save()} disabled={saving} className="px-4 py-1.5 rounded-lg bg-amber-500 text-foreground hover:bg-amber-600 text-sm disabled:opacity-50">
            {saving ? 'Saving...' : plan ? 'Update Plan' : 'Create Plan'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── AI Recommendations Panel ────────────────────────────────

function AIRecommendationsPanel() {
  const [role, setRole] = useState('');
  const [skills, setSkills] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ candidates: AICandidate[]; ai_recommendations: string[] } | null>(null);
  const [expanded, setExpanded] = useState(false);

  async function recommend() {
    if (!role.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await fetch('/api/people/succession/ai-recommend', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          role,
          required_skills: skills.split(',').map(s => s.trim()).filter(Boolean),
        }),
      });
      if (res.ok) setResult(await res.json() as { candidates: AICandidate[]; ai_recommendations: string[] });
    } catch { /* silent */ }
    finally { setLoading(false); }
  }

  return (
    <div className="glass rounded-xl p-5">
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-amber-500" />
          <h3 className="text-sm font-semibold text-foreground">AI Succession Recommendations</h3>
        </div>
        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded && (
        <div className="mt-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Role to fill</label>
              <input value={role} onChange={e => setRole(e.target.value)} placeholder="VP of Engineering" className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Required skills (comma-separated)</label>
              <input value={skills} onChange={e => setSkills(e.target.value)} placeholder="leadership, TypeScript, cloud" className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground" />
            </div>
          </div>
          <button onClick={() => void recommend()} disabled={loading || !role.trim()} className="px-4 py-1.5 rounded-lg bg-amber-500 text-foreground hover:bg-amber-600 text-sm disabled:opacity-50">
            {loading ? 'Analyzing...' : 'Get AI Recommendations'}
          </button>

          {result && (
            <div className="space-y-3 pt-2">
              {result.ai_recommendations.length > 0 && (
                <div className="space-y-1">
                  {result.ai_recommendations.map((rec, i) => (
                    <p key={i} className="text-xs text-foreground bg-amber-500/10 rounded px-3 py-2">{rec}</p>
                  ))}
                </div>
              )}
              <div className="space-y-2">
                {result.candidates.map(c => (
                  <div key={c.employee_id} className="flex items-center justify-between gap-3 p-2 rounded-lg bg-surface-3">
                    <div>
                      <p className="text-sm font-medium text-foreground">{c.employee_name}</p>
                      <p className="text-xs text-muted-foreground">{c.title} · {c.department}</p>
                    </div>
                    <span className={`text-sm font-bold ${c.fit_score >= 70 ? 'text-green-400' : c.fit_score >= 40 ? 'text-amber-400' : 'text-red-400'}`}>
                      {c.fit_score}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────

type Tab = 'pipeline' | 'risks';

export function SuccessionPlanningPage() {
  const [plans, setPlans] = useState<SuccessionPlan[]>([]);
  const [risks, setRisks] = useState<KeyPersonRisk[]>([]);
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>('pipeline');
  const [showModal, setShowModal] = useState(false);
  const [editPlan, setEditPlan] = useState<SuccessionPlan | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [plansRes, risksRes, dashRes] = await Promise.all([
        fetch('/api/people/succession/plans'),
        fetch('/api/people/succession/risks'),
        fetch('/api/people/succession/dashboard'),
      ]);
      if (plansRes.ok) {
        const data = await plansRes.json() as { plans: SuccessionPlan[] };
        setPlans(data.plans);
      }
      if (risksRes.ok) {
        const data = await risksRes.json() as { risks: KeyPersonRisk[] };
        setRisks(data.risks);
      }
      if (dashRes.ok) {
        setDashboard(await dashRes.json() as Dashboard);
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function deletePlan(id: string) {
    try {
      await fetch(`/api/people/succession/plans/${id}`, { method: 'DELETE' });
      await load();
    } catch { /* silent */ }
  }

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <GitBranch className="w-5 h-5 text-amber-500" />
          <h1 className="text-xl font-bold text-foreground">Succession Planning</h1>
        </div>
        <button
          onClick={() => { setEditPlan(null); setShowModal(true); }}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500 text-foreground hover:bg-amber-600 text-sm"
        >
          <Plus className="w-3.5 h-3.5" /> New Plan
        </button>
      </div>

      {/* Dashboard stats */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="glass rounded-xl p-4">
            <p className="text-xs text-muted-foreground mb-1">Roles Covered</p>
            <p className="text-2xl font-bold text-foreground">{dashboard.roles_covered}/{dashboard.total_roles}</p>
          </div>
          <div className="glass rounded-xl p-4">
            <p className="text-xs text-muted-foreground mb-1">Avg Readiness</p>
            <p className={`text-2xl font-bold ${dashboard.avg_readiness_score >= 70 ? 'text-green-400' : dashboard.avg_readiness_score >= 40 ? 'text-amber-400' : 'text-red-400'}`}>
              {dashboard.avg_readiness_score}%
            </p>
          </div>
          <div className="glass rounded-xl p-4">
            <p className="text-xs text-muted-foreground mb-1">Critical Gaps</p>
            <p className={`text-2xl font-bold ${dashboard.critical_gaps > 0 ? 'text-red-400' : 'text-green-400'}`}>{dashboard.critical_gaps}</p>
          </div>
          <div className="glass rounded-xl p-4">
            <p className="text-xs text-muted-foreground mb-1">Readiness Mix</p>
            <div className="flex items-center gap-1 text-xs mt-1">
              <span className="text-green-400">{dashboard.readiness_breakdown.ready} ready</span>
              <span className="text-muted-foreground">·</span>
              <span className="text-amber-400">{dashboard.readiness_breakdown.developing} dev</span>
              <span className="text-muted-foreground">·</span>
              <span className="text-red-400">{dashboard.readiness_breakdown.not_ready} not</span>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1">
        {(['pipeline', 'risks'] as Tab[]).map(t => (
          <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 rounded-lg text-sm capitalize transition-colors ${tab === t ? 'bg-amber-500/20 text-amber-400' : 'text-muted-foreground hover:text-foreground hover:bg-surface-3'}`}>
            {t === 'pipeline' ? 'Talent Pipeline' : `Key-Person Risks (${risks.length})`}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-muted-foreground text-sm">Loading...</div>
      ) : tab === 'pipeline' ? (
        <div className="space-y-4">
          {plans.length === 0 ? (
            <div className="glass rounded-xl p-12 text-center">
              <GitBranch className="w-12 h-12 text-amber-500/20 mx-auto mb-3" />
              <p className="text-foreground font-medium mb-1">No succession plans yet</p>
              <p className="text-sm text-muted-foreground mb-4">Create plans to visualize your talent pipeline.</p>
              <button onClick={() => setShowModal(true)} className="px-4 py-2 rounded-lg bg-amber-500 text-foreground hover:bg-amber-600 text-sm">
                Create First Plan
              </button>
            </div>
          ) : (
            <>
              {/* Pipeline SVG */}
              <div className="glass rounded-xl p-5 overflow-x-auto">
                <h3 className="text-sm font-semibold text-foreground mb-4">Talent Pipeline</h3>
                <TalentPipeline plans={plans} />
              </div>

              {/* Plans table */}
              <div className="glass rounded-xl overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left px-5 py-2.5 font-medium text-muted-foreground">Role</th>
                      <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Successor</th>
                      <th className="text-center px-4 py-2.5 font-medium text-muted-foreground">Readiness</th>
                      <th className="text-center px-4 py-2.5 font-medium text-muted-foreground">Risk</th>
                      <th className="text-right px-5 py-2.5 font-medium text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plans.map(p => (
                      <tr key={p.id} className="border-b border-border/50 hover:bg-surface-3/50">
                        <td className="px-5 py-2.5">
                          <p className="font-medium text-foreground">{p.role}</p>
                          <p className="text-muted-foreground">{p.role_holder_name ?? 'Vacant'} · {p.department_name}</p>
                        </td>
                        <td className="px-4 py-2.5">
                          <p className="text-foreground">{p.primary_successor_name ?? <span className="text-red-400">None designated</span>}</p>
                          {p.shadow_name && <p className="text-muted-foreground">Shadow: {p.shadow_name}</p>}
                        </td>
                        <td className="text-center px-4 py-2.5"><ReadinessBadge readiness={p.readiness} /></td>
                        <td className="text-center px-4 py-2.5">
                          <span className={`font-bold ${riskColor(p.risk_score)}`}>{p.risk_score}</span>
                        </td>
                        <td className="text-right px-5 py-2.5">
                          <div className="flex items-center justify-end gap-2">
                            <button onClick={() => { setEditPlan(p); setShowModal(true); }} className="text-xs text-muted-foreground hover:text-foreground">Edit</button>
                            <button onClick={() => void deletePlan(p.id)} className="text-xs text-red-400 hover:text-red-300">Delete</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      ) : (
        /* Key-person risks */
        <div className="space-y-3">
          {risks.length === 0 ? (
            <div className="glass rounded-xl p-8 text-center text-muted-foreground text-sm">No key-person risks identified</div>
          ) : risks.map(r => (
            <div key={r.employee_id} className={`glass rounded-xl p-4 border-l-2 ${r.risk_score >= 70 ? 'border-red-500' : 'border-amber-500'}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <p className="text-sm font-semibold text-foreground">{r.employee_name}</p>
                  <p className="text-xs text-muted-foreground">{r.title} · {r.department}</p>
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {r.risk_reasons.map((reason, i) => (
                      <span key={i} className="flex items-center gap-1 text-xs text-muted-foreground bg-surface-3 rounded px-1.5 py-0.5">
                        <AlertTriangle className="w-2.5 h-2.5 text-amber-400" /> {reason}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className={`text-xl font-bold ${riskColor(r.risk_score)}`}>{r.risk_score}</p>
                  <p className="text-xs text-muted-foreground">risk score</p>
                  {r.has_succession_plan && r.successor_readiness && (
                    <ReadinessBadge readiness={r.successor_readiness} />
                  )}
                  {!r.has_succession_plan && (
                    <button onClick={() => { setEditPlan(null); setShowModal(true); }} className="mt-1 text-xs text-amber-400 hover:text-amber-300">
                      + Add Plan
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* AI Recommendations */}
      <AIRecommendationsPanel />

      {showModal && (
        <PlanModal
          plan={editPlan}
          onClose={() => { setShowModal(false); setEditPlan(null); }}
          onSaved={() => void load()}
        />
      )}
    </div>
  );
}
